import { useEffect } from 'react'
import { Link, useParams, useNavigate, useOutletContext } from 'react-router-dom'
import { useComputed, useSignal } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Form from 'react-bootstrap/Form'
import { InputGroup, NavItem } from 'react-bootstrap'
import Nav from 'react-bootstrap/Nav'
import Pagination from 'react-bootstrap/Pagination'
import Stack from 'react-bootstrap/Stack'
import Table from 'react-bootstrap/Table'

import ConfirmationModal from '../../components/ConfirmationModal.jsx'
import CopyRowAsJSON from '../../components/CopyRowAsJSON.jsx'
import DebugAccordion from '../../components/DebugAccordion.jsx'
import DeleteTaskModal from '../../components/DeleteTaskModal.jsx'
import DownloadProgress from '../../components/DownloadProgress.jsx'
import GenericTooltip from '../../components/GenericTooltip.jsx'
import Icon from '../../components/Icon.jsx'
import LoadingAnimation from '../../components/LoadingAnimation.jsx'
import PageSizeSelector from '../../components/PageSizeSelector.jsx'
import PageTitle from '../../components/PageTitle.jsx'
import TaskListFilteringModal from './components/TaskListFilteringModal.jsx'
import TasksInfoModal from './components/TasksInfoModal.jsx'
import TimestampWithUTCTooltip from '../../components/TimestampWithUTCTooltip.jsx'

import { NotificationTypes, addNotification } from '../../state/actions/notificationActions.js'
import { fetchTaskList, deleteTask, exportTasks } from '../../state/actions/taskActions.js'
import { updatePageSize, updateTaskListFilter } from '../../state/actions/userSettingsActions.js'

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal } from '../../state/signals/businessEntitySignals.js'
import { taskListSignal, taskListLoadedSignal } from '../../state/signals/taskSignals.js'
import { pageSizeSignal as rowsPerPageSignal, debugSignal, taskListFiltersValueSignal, taskListFiltersCountSignal, selectedBusinessEntityIdSignal } from '../../state/signals/userSettingsSignals.js'

import { SortDirectionType } from '../../utils/constants.js'
import { saveFile } from '../../utils/file.js'
import { getDefaultTaskListFilters } from '../../utils/filters.js'
import { computeSortedData } from '../../utils/table.js'
import { canDeleteTask } from '../../utils/utils.js'

export default function TasksList() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()

    useEffect(() => {
        if (userPermissionsSummary && userPermissionsSummary.value && !userPermissionsSummary.value.canViewTasks) {
            addNotification('You are not authorized to view Tasks for this account.', NotificationTypes.ERROR, true)
            navigate(`/`)
        }
    }, [userPermissionsSummary, userPermissionsSummary.value, navigate])

    const canEditComputed = useComputed(() => {
        return userPermissionsSummary?.value?.canEditTasks
    })

    const showDownloadProgressSignal = useSignal(false)
    const downloadProgressMessageSignal = useSignal('')
    const showExportConfirmationModalSignal = useSignal(false)
    const showTasksInfoModalSignal = useSignal(false)

    const currentPageSignal = useSignal(1)
    const filterStringSignal = useSignal("")
    const sortColumnSignal = useSignal('')
    const isSelectAllSignal = useSignal(false)
    const selectedRowsSignal = useSignal([])
    const selectedRowsCountLabelComputed = useComputed(() => {
        return `(${selectedRowsSignal.value.length})`
    })

    const sortDirectionSignal = useSignal(SortDirectionType.DESCENDING)

    useEffect(() => {
        if (selectedBusinessEntityIdSignal.value !== beId) {
            updateTaskListFilter(getDefaultTaskListFilters())
        }
        fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
    }, [beId, config, accessToken])

    const filteredDataComputed = useComputed(() => {
        if (!filterStringSignal.value) {
            return taskListSignal.value ? [...taskListSignal.value] : []
        }

        // Convert the filter to lowercase
        const lowerCaseFilter = filterStringSignal.value.toLowerCase()

        // Filter the data based on the filter text
        return taskListSignal.value.filter(item => {

            // Limit search to these fields
            let searchData = {
                componentMerchant: item.componentMerchant,
                componentNickname: item.componentNickname,
                utgStatus: item.utgStatus,
                utgVersion: item.utgVersion,
                agentVersion: item.agentVersion,
                // configStatus: item.configStatus, // not visible on this page
                componentProfileName: item.componentProfileName,
                componentProfileVersion: item.componentProfileVersion,
                componentLocation: item.componentLocation,
                componentLane: item.componentLane,
                componentFileId: item.componentFileId,
                machineDnsName: item.machineDnsName,
                machineStatus: item.machineStatus,
                machineStatusEntityLastKnownCommunication: item.machineStatusEntityLastKnownCommunication,
                machineStatusEntityNextPredictedCommunication: item.machineStatusEntityNextPredictedCommunication,
                machineNickname: item.machineNickname,
                businessEntityId: item.businessEntityId,
                standardTimeZone: item.machineStatusEntityStandardTimeZone,
                windowsVersion: item.machineStatusEntityWindowsVersion,

                componentPermanentId: item.componentPermanentId,
                machinePermanentId: item.machinePermanentId,

                taskBulkLabel: item.taskBulkLabel,
                taskId: item.taskId,
                taskLastUpdatedAt: item.taskLastUpdatedAt,
                taskRunAfter: item.taskRunAfter,
                taskStatusName: item.taskStatusName,
                taskTypeName: item.taskTypeName
            }

            return Object.values(searchData).some(value => {
                if (value && (typeof (value) == 'string')) {
                    return value.toString().toLowerCase().includes(lowerCaseFilter)
                }
            })
        })
    })

    const sortedDataComputed = useComputed(computeSortedData(filteredDataComputed, sortColumnSignal, sortDirectionSignal))

    const totalPagesComputed = useComputed(() => {
        return Math.ceil(filteredDataComputed.value.length / parseInt(rowsPerPageSignal.value, 10))
    })

    const displayDataComputed = useComputed(() => {
        // Calculate the starting and ending indexes for pagination
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value

        return sortedDataComputed.value.slice(startIndex, endIndex)
    })
    const startDisplayRowNumberComputed = useComputed(() => {
        return ((currentPageSignal.value - 1) * rowsPerPageSignal.value) + 1
    })
    const endDisplayRowNumberComputed = useComputed(() => {
        const startIndex = (currentPageSignal.value - 1) * rowsPerPageSignal.value
        const endIndex = startIndex + rowsPerPageSignal.value
        return endIndex < filteredDataComputed.value.length ? endIndex : filteredDataComputed.value.length
    })

    const accordionData = [
        { name: 'Tasks', data: taskListSignal.value },
        { name: 'Display Data', data: displayDataComputed.value },
        { name: 'Sorted Data', data: sortedDataComputed.value },
        { name: 'Filtered Data', data: filteredDataComputed.value },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handlePageSizeSelect = (v) => {
        currentPageSignal.value = 1
        updatePageSize(parseInt(v, 10))
    }
    const handleFilterStringChange = (e) => {
        currentPageSignal.value = 1
        filterStringSignal.value = e.currentTarget.value
    }

    // Task Filtering Modal
    const showTaskFilteringModalSignal = useSignal(false)
    const handleCancelTaskFiltering = () => showTaskFilteringModalSignal.value = false
    const handleApplyTaskFilters = (predicates) => {
        showTaskFilteringModalSignal.value = false
        updateTaskListFilter(predicates)
        fetchTaskList(beId, predicates, config, accessToken)
    }
    const handleClearFilter = () => {
        updateTaskListFilter({Predicates:[]})
        fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
    }

    const showRemoveTaskModalSignal = useSignal(false)
    const handleCancelRemoveTask = () => showRemoveTaskModalSignal.value = false
    const handleSubmitModalRemoveTask = () => {
        showRemoveTaskModalSignal.value = false

        let promises = []
        for (let i = 0; i < selectedRowsSignal.value.length; i++) {
            try {
                promises.push(deleteTask(selectedRowsSignal.value[i].businessEntityId, selectedRowsSignal.value[i].machinePermanentId, selectedRowsSignal.value[i].taskId, config, accessToken, true))
            } catch (error) {
                addNotification(`Unable to remove all selected Tasks: ${error.toString()}`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            }
        }
        Promise.all(promises)
            .then(async () => {
                selectNone()
                addNotification('The selected Tasks were successfully removed.', NotificationTypes.SUCCESS, false)
                //fetchTaskList(beId, taskListFiltersValueSignal.value, config, accessToken)
            })
            .catch((error) => {
                addNotification(`Unable to remove all selected Tasks: caught exception ${error.toString()}`, NotificationTypes.ERROR, false, 'Remove Tasks Failed')
            })
    }

    const handleRemoveTask = (item) => {
        if (item) {
            // if array does not contain item, add item
            if (!selectedRowsSignal.value.includes(item)) {
                selectedRowsSignal.value = [...selectedRowsSignal.value, item]
            }
            showRemoveTaskModalSignal.value = true
        }
    }
    const handleTaskExport = async () => {
        // set showDownloadProgress to true to show spinner
        showDownloadProgressSignal.value = true
        downloadProgressMessageSignal.value = 'Preparing to export tasks'
        exportTasks(beId, config, accessToken).then(
            (exportTasksResponse) => {
                saveFile(exportTasksResponse, "tasks.csv")
                addNotification('Exporting tasks. Your download should begin shortly.', NotificationTypes.SUCCESS, true)
            }
        ).catch(
            (error) => {
                console.log(error)
                addNotification(`Unable to export tasks. ${error}`, NotificationTypes.ERROR, true)
            }
        ).finally(
            () => {
                showDownloadProgressSignal.value = false
            }
        )
    }

    const selectNone = () => {
        // deselect all
        selectedRowsSignal.value = []
        isSelectAllSignal.value = false
    }

    const handleSelectAll = () => {

        if (isSelectAllSignal.value) {
            selectNone()
            return
        }

        // select all rows from filtered data
        isSelectAllSignal.value = true
        selectedRowsSignal.value = filteredDataComputed.value.map(item => {
            return item
        })
    }

    const handleSelectRow = (item) => {
        // Check if the item is already selected
        const wasItemSelected = selectedRowsSignal.value.some(v => v.taskId === item.taskId);

        if (wasItemSelected) {
            // User wants to deselect, so filter out the item
            selectedRowsSignal.value = selectedRowsSignal.value.filter(v => v.taskId !== item.taskId);
        } else {
            // Add the item to the selectedRows array
            selectedRowsSignal.value = [...selectedRowsSignal.value, item];
        }
    }

    const handleHeaderTableClick = datafield => {
        // if already sorted on this column then toggle sort direction
        if (datafield == sortColumnSignal.value) {
            sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
            return
        }

        // update sort column - the computed functions will then run and render the sorted data
        sortColumnSignal.value = datafield
    }

    const handleCancelModalTasksInfo = () => {
        showTasksInfoModalSignal.value = false
    }

    function TableRows({ items }) {
        if (!taskListLoadedSignal.value) {
            return <tr><td colSpan={23}><LoadingAnimation /></td></tr>
        }
        if (items && items.length) {
            const rows = items.map(item => {
                return <TableRow item={item} key={`${item.taskId}`} />
            })
            return rows
        }
        else if (items) {
            return <tr><td colSpan="23" className="text-center">No tasks found</td></tr>
        }
    }

    function TableRow({ item }) {
        return (
            <tr>
                <td><Form.Check aria-label="Select Row" checked={selectedRowsSignal.value.some(row => row.taskId === item.taskId)} onChange={() => handleSelectRow(item)} /></td>
                <td>
                    <Dropdown>
                        <Dropdown.Toggle variant="link" size="sm" id="actions">
                            <Icon icon="gear" height={16} width={16}></Icon>
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            <CopyRowAsJSON
                                item={item}
                                messageClosure={
                                    (item) => {
                                        return `The data for Task Id: ${item.taskId} (${item.taskTypeName}) has been copied to your clipboard.`
                                    }
                                }
                            />
                            {
                                canEditComputed.value && (
                                    canDeleteTask(item.taskStatus) ? (
                                        <Dropdown.Item onClick={() => handleRemoveTask(item)}>
                                            <Icon icon="trash" height={16} width={16}></Icon>&nbsp;Delete Task
                                        </Dropdown.Item>
                                    ) : (
                                        <Dropdown.Item>
                                            Can&apos;t delete task&nbsp;
                                            <GenericTooltip tip="Tasks can only be deleted if they are in one of the following states: Requested, Accepted, Planned.">
                                                    <Icon color="#0d6efd" icon="info-circle" height={16} width={16} />
                                            </GenericTooltip>
                                        </Dropdown.Item>
                                    )
                                )
                            }
                        </Dropdown.Menu>
                    </Dropdown>
                </td>
                <td>{item.componentMerchant}</td>
                <td>{item.machineDnsName}</td>
                <td><Link to={`../../be/${item.businessEntityId}/utgs/${item.componentId}`}>{item.componentNickname}</Link></td>
                <td>{item.taskTypeName}</td>
                <td><TimestampWithUTCTooltip>{item.taskRunAfter}</TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip>{item.taskLastUpdatedAt}</TimestampWithUTCTooltip></td>
                <td>{item.taskStatusName}</td>
                <td>{item.machineStatus}</td>
                <td>{item.utgStatus}</td>
                <td><TimestampWithUTCTooltip>{item.machineStatusEntityLastKnownCommunication}</TimestampWithUTCTooltip></td>
                <td><TimestampWithUTCTooltip>{item.machineStatusEntityNextPredictedCommunication}</TimestampWithUTCTooltip></td>
                <td>{item.utgVersion}</td>
                <td>{item.agentVersion}</td>
                <td>{item.componentProfileName}</td>
                <td>{item.componentProfileVersion}</td>
                <td>{item.componentFileId}</td>
                <td>{item.componentLocation}</td>
                <td>{item.componentLane}</td>
                <td>{item.machineNickname}</td>
                <td>{item.taskBulkLabel}</td>
                <td>{currentBusinessEntitySignal.value.name}</td>
                <td>{item.machineStatusEntityStandardTimeZone}</td>
                <td>{item.machineStatusEntityWindowsVersion}</td>
            </tr>
        )
    }

    function PaginationItems() {
        let items = []
        for (let i = 1; i <= totalPagesComputed.value; i++) {
            items.push(
                <Pagination.Item key={i} active={i === currentPageSignal.value} onClick={() => currentPageSignal.value = i}>{i}</Pagination.Item>
            )
        }
        return items
    }

    function TableColumnHeader({ title, sortable, datafield }) {
        // non-sortable columns do not have click handlers
        if (!sortable) {
            return (
                <th>{title}</th>
            )
        }

        // sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
        if (datafield != sortColumnSignal.value) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
            )
        }

        // datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
        if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
            return (
                <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                    <Icon icon="caret-down" width="16" height="16"></Icon>
                </th>
            )
        }

        // else must be ascending
        return (
            <th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
                <Icon icon="caret-up" width="16" height="16"></Icon>
            </th>
        )
    }

    return (
        <>
            <DownloadProgress showSignal={showDownloadProgressSignal} messageSignal={downloadProgressMessageSignal} />
            <ConfirmationModal
                show={showExportConfirmationModalSignal.value}
                handleCancel={
                    () => showExportConfirmationModalSignal.value = false
                }
                handleConfirm={
                    () => {
                        showExportConfirmationModalSignal.value = false
                        handleTaskExport()
                    }
                }
                message={'Export may take a few minutes to download ALL data on this tab. Are you sure you want to continue?'}
                title={"Export Tasks"}
            />
            <DeleteTaskModal show={showRemoveTaskModalSignal.value} handleCancel={() => handleCancelRemoveTask()} handleConfirm={() => { handleSubmitModalRemoveTask(); }} selectedTasks={selectedRowsSignal} />
            <PageTitle title='Tasks List' canEditSignal={canEditComputed} />
            <Nav className="justify-content-between px-3 py-1 my-2 bg-light">
                <Nav.Item className="me-2">
                    <InputGroup>
                        <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => handleClearFilter()}>
                            <Icon icon='trash' width="16" height="16"></Icon>
                        </Button>
                        <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showTaskFilteringModalSignal.value = true}><Icon icon='funnel' width="16" height="16"></Icon>&nbsp;Filters ({taskListFiltersCountSignal.value})</Button>
                    </InputGroup>
                </Nav.Item>
                <Nav.Item className="me-auto">
                    <PageSizeSelector pageSizeSignal={rowsPerPageSignal} handlePageSizeSelect={handlePageSizeSelect} />
                </Nav.Item>
                <Nav.Item className="me-2" style={{maxWidth:"calc(100% - 407.25px)"}}>
                    <Form.Control type="text" placeholder="Search" size="sm" htmlSize="75" onChange={handleFilterStringChange} />
                </Nav.Item>
                <Nav.Item className="me-2">
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => { showTasksInfoModalSignal.value = true }}>
                        <Icon icon="info-circle" height={16} width={16}></Icon>&nbsp;Help
                    </Button>
                </Nav.Item>
                <Nav.Item>
                    <Button size="sm" variant="outline-secondary" className="d-flex align-items-center justify-content-center" onClick={() => showExportConfirmationModalSignal.value = true}><Icon icon='download' width="16" height="16"></Icon>&nbsp;Download data as CSV</Button>
                </Nav.Item>
            </Nav >
            <Table responsive striped hover size="sm" className="cc-table">
                <thead>
                    <tr>
                        <td><Form.Check type="checkbox" id="select-all-switch" aria-label="Select All" label={selectedRowsCountLabelComputed.value} checked={isSelectAllSignal.value} onChange={() => handleSelectAll()} /></td>
                        <TableColumnHeader title={'Action'} sortable={false} datafield={''}></TableColumnHeader>
                        <TableColumnHeader title={'Merchant'} sortable={true} datafield={'componentMerchant'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine DNS Name'} sortable={true} datafield={'machineDnsName'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Nickname'} sortable={true} datafield={'componentNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Type'} sortable={true} datafield={'taskTypeName'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Run Date'} sortable={true} datafield={'taskRunAfter'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Updated'} sortable={true} datafield={'taskLastUpdatedAt'}></TableColumnHeader>
                        <TableColumnHeader title={'Task State'} sortable={true} datafield={'taskStatusName'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Status'} sortable={true} datafield={'machineStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Status'} sortable={true} datafield={'utgStatus'}></TableColumnHeader>
                        <TableColumnHeader title={'Last Known Communication'} sortable={true} datafield={'machineStatusEntityLastKnownCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'Next Predicted Communication'} sortable={true} datafield={'machineStatusEntityNextPredictedCommunication'}></TableColumnHeader>
                        <TableColumnHeader title={'UTG Version'} sortable={true} datafield={'utgVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Agent Version'} sortable={true} datafield={'agentVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile'} sortable={true} datafield={'componentProfileName'}></TableColumnHeader>
                        <TableColumnHeader title={'Profile Version'} sortable={true} datafield={'componentProfileVersion'}></TableColumnHeader>
                        <TableColumnHeader title={'File Id'} sortable={true} datafield={'componentFileId'}></TableColumnHeader>
                        <TableColumnHeader title={'Location/Serial'} sortable={true} datafield={'componentLocation'}></TableColumnHeader>
                        <TableColumnHeader title={'Lane'} sortable={true} datafield={'componentLane'}></TableColumnHeader>
                        <TableColumnHeader title={'Machine Nickname'} sortable={true} datafield={'machineNickname'}></TableColumnHeader>
                        <TableColumnHeader title={'Task Label'} sortable={true} datafield={'taskBulkLabel'}></TableColumnHeader>
                        <TableColumnHeader title={'Parent Account'} sortable={true} datafield={'parentName'}></TableColumnHeader>
                        <TableColumnHeader title={'Time Zone'} sortable={true} datafield={'machineStatusEntityStandardTimeZone'}></TableColumnHeader>
                        <TableColumnHeader title={'Windows Version'} sortable={true} datafield={'machineStatusEntityWindowsVersion'}></TableColumnHeader>
                    </tr>
                </thead>
                <tbody><TableRows items={displayDataComputed.value} /></tbody>
                {
                    displayDataComputed.value.length > 0 && (
                        <tfoot><tr><td className="fs-6" colSpan={19}>Displaying {startDisplayRowNumberComputed.value}-{endDisplayRowNumberComputed.value} of {filteredDataComputed.value.length}</td></tr></tfoot>
                    )
                }
            </Table>
            <Pagination className="px-3 d-flex flex-wrap" size="sm"><PaginationItems /></Pagination>
            <DebugAccordion data={accordionData} debug={debugSignal} />

            <TaskListFilteringModal
                show={showTaskFilteringModalSignal.value}
                handleCancel={handleCancelTaskFiltering}
                handleSubmit={handleApplyTaskFilters}
            ></TaskListFilteringModal>

            <TasksInfoModal
                showSignal={showTasksInfoModalSignal}
                handleCancel={handleCancelModalTasksInfo}
                referenceGuid={config && config.referenceGuid ? config.referenceGuid : null}
            ></TasksInfoModal>
        </>
    )
}